import React, { useState, useContext } from "react";
import { useParams } from "react-router-dom";

import { AssetContext } from "../contexts/AssetListContext.js";

import PdfViewerModal from "../components/PdfViewerModal.js";
import PointerraModal from "../components/PointerraModal.js";
import ImageGallery from "../components/ImageGallery";

import detailedPDF from "../assets/icons/detailed_pdf_icon.svg";
import digitalTwinIcon from "../assets/icons/digital_twin.png";
import assetLocation from "../assets/icons/asset_location_icon.svg";
import photoIcon from "../assets/icons/inspection_photo_icon.svg";
import issueDescriptionIcon from "../assets/icons/issue_description.svg";
import BackButton from "../components/BackButton.js";
import WorkspaceYearSelect from "../components/WorkspaceYearSelect.js";

import { RiskIcon } from "../components/RiskRateSection.js";
import {
    getDefectPhotoUrl,
    getDefectReportUrl,
    getClosureReportUrl,
} from "../utils/mediaUrls.js";
import { getDefectEmbedUrl } from "../utils/pointerra.js";

function DefectDetails() {
    const { defect_id } = useParams(); // Get defect_id from URL params
    const {
        assets,
        defects,
        selectedYear,
        setSelectedYear,
        availableYears = [],
        latestYear,
        latestDefectIds,
        isDefectsLoading = false,
    } = useContext(AssetContext);

    // Image Gallery state
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const openGallery = (index) => {
        setSelectedImageIndex(index);
        setIsGalleryOpen(true);
    };

    const closeGallery = () => {
        setIsGalleryOpen(false);
    };

    // PDF Viewer states
    const [isModalOpen, setIsModalOpen] = useState(false);
    const openModal = () => setIsModalOpen(true);
    const closeModal = () => setIsModalOpen(false);

    // Filtering the defect using defect_id
    const defect = defects.find((def) => def.defect_id === defect_id);
    const isOutdatedDefect =
        Boolean(defect) &&
        String(selectedYear) !== String(latestYear) &&
        !latestDefectIds?.has(String(defect_id));

    // ADD THESE NEW STATES:
    // Remedial procedure modal state
    const [isRemedialOpen, setIsRemedialOpen] = useState(false);

    // Closure report modal state
    const [isClosureOpen, setIsClosureOpen] = useState(false);

    // Pointerra 3D view modal state
    const [is3DOpen, setIs3DOpen] = useState(false);

    if (isDefectsLoading) {
        return <p>Loading defect data...</p>;
    }

    if (!defect) {
        return (
            <div className="defect-details-container">
                <div className="defect-details-title">
                    <h2>{defect_id}</h2>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <WorkspaceYearSelect
                            id="defect-details-year-select"
                            selectedYear={selectedYear}
                            onYearChange={setSelectedYear}
                            availableYears={availableYears}
                            disabled={
                                availableYears.length === 0 || isDefectsLoading
                            }
                        />
                        <BackButton />
                    </div>
                </div>
                <p>
                    No defect found for {defect_id} in {selectedYear}.
                </p>
            </div>
        );
    }

    const remedialRef = (defect.repair_ref || "").trim();
    const closureRef = (defect.closure_report || "").trim();
    const embedUrl = getDefectEmbedUrl(assets, defect);
    const images = [
        getDefectPhotoUrl(defect, 1),
        getDefectPhotoUrl(defect, 2),
        getDefectPhotoUrl(defect, 3),
    ];

    return (
        <div className="defect-details-container">
            <div className="defect-details-title">
                <h2>{defect_id}</h2>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}
                >
                    <WorkspaceYearSelect
                        id="defect-details-year-select"
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        availableYears={availableYears}
                        disabled={
                            availableYears.length === 0 || isDefectsLoading
                        }
                    />
                    <BackButton />
                </div>
            </div>
            {isOutdatedDefect && (
                <div
                    style={{
                        backgroundColor: "#FFF4E5",
                        border: "1px solid #FFD8A8",
                        color: "#8A5A00",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        marginBottom: "12px",
                    }}
                >
                    This defect is outdated (it does not appear in {latestYear}
                    ).
                </div>
            )}

            <div className="defect-details-section">
                <div className="defect-details-main-section">
                    <img src={getDefectPhotoUrl(defect, 2)} alt="Asset Photo" />
                </div>
                <div className="defect-details-side-section">
                    <div className="report-section">
                        <div>
                            <img
                                src={detailedPDF}
                                alt="Alert icon"
                                style={{ width: "40px", height: "40px" }}
                            ></img>
                            <div className="report-section-title">
                                <h5>Report</h5>
                                <p>Detailed PDF</p>
                            </div>
                        </div>
                        <button onClick={openModal}>View</button>
                    </div>
                    {embedUrl && (
                        <div
                            className="report-section"
                            style={{ marginTop: "16px" }}
                        >
                            <div>
                                <img
                                    src={digitalTwinIcon}
                                    alt="Digital twin icon"
                                    style={{ width: "40px", height: "40px" }}
                                />
                                <div className="report-section-title">
                                    <h5>Digital Twin</h5>
                                    <p>3D Point Cloud</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIs3DOpen(true)}
                                title="Open the digital twin"
                            >
                                View
                            </button>
                        </div>
                    )}
                    <div className="asset-location-section">
                        <div className="asset-location-section-one">
                            <img
                                src={assetLocation}
                                alt="Alert icon"
                                style={{ width: "30px", height: "30px" }}
                            ></img>
                            <h5 style={{ color: "#333333" }}>Asset Location</h5>
                        </div>
                        <div className="asset-location-section-two">
                            <h6 style={{ color: "#333333", fontSize: "16px" }}>
                                Asset:
                            </h6>
                            <p style={{ color: "#767676" }}>
                                {defect.asset_id} - {defect.asset_name}
                            </p>
                        </div>
                        <div className="asset-location-section-two">
                            <h6 style={{ color: "#333333", fontSize: "16px" }}>
                                Component:
                            </h6>
                            <p
                                style={{
                                    color: "#767676",
                                    paddingLeft: "10px",
                                }}
                            >
                                {defect.component || defect.area}
                            </p>
                        </div>
                    </div>

                    <div className="defect-risk-rate">
                        <RiskIcon riskLevel={defect.risk_rate} size={30} />

                        <div>
                            <h6 style={{ color: "#333333" }}>
                                {defect.defect_type}
                            </h6>
                            <p style={{ color: "#757575" }}>
                                {defect.risk_rate} Risk
                            </p>
                        </div>
                    </div>
                    <div className="defect-issue-description">
                        <img
                            src={issueDescriptionIcon}
                            alt=""
                            style={{ width: "30px" }}
                            width={40}
                            height={40}
                        />
                        <div className="defect-issue-description-text">
                            <h6>Issue Description</h6>
                            <p>
                                {defect.issue_description ||
                                    "No issue description provided"}
                            </p>
                        </div>
                    </div>
                    <div className="inspection-photos">
                        <div className="inspection-photos-title">
                            <img src={photoIcon}></img>
                            <h5 style={{ color: "#333333" }}>
                                Inspection Photos
                            </h5>
                        </div>
                        <div className="inspection-photos-selection">
                            <div>
                                <img
                                    src={images[0]}
                                    alt="Defect Photo 1"
                                    onClick={() => openGallery(0)}
                                    style={{ cursor: "pointer" }}
                                />
                            </div>
                            <div>
                                <img
                                    src={images[1]}
                                    alt="Defect Photo 2"
                                    onClick={() => openGallery(1)}
                                    style={{ cursor: "pointer" }}
                                />
                            </div>
                            <div>
                                <img
                                    src={images[2]}
                                    alt="Defect Photo 3"
                                    onClick={() => openGallery(2)}
                                    style={{ cursor: "pointer" }}
                                />
                            </div>
                        </div>
                        {isGalleryOpen && (
                            <ImageGallery
                                images={images}
                                isOpen={isGalleryOpen}
                                selectedImageIndex={selectedImageIndex}
                                onClose={closeGallery}
                            />
                        )}
                    </div>
                    {remedialRef && (
                        <div
                            className="report-section"
                            style={{ marginTop: "16px" }}
                        >
                            <div>
                                <img
                                    src={detailedPDF}
                                    alt="PDF icon"
                                    style={{ width: "40px", height: "40px" }}
                                />
                                <div className="report-section-title">
                                    <h5>Remedial </h5>
                                    <p>Procedure</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsRemedialOpen(true)}
                                title="View remedial procedure"
                            >
                                View
                            </button>
                        </div>
                    )}

                    {closureRef && (
                        <div
                            className="report-section"
                            style={{ marginTop: "16px" }}
                        >
                            <div>
                                <img
                                    src={detailedPDF}
                                    alt="PDF icon"
                                    style={{ width: "40px", height: "40px" }}
                                />
                                <div className="report-section-title">
                                    <h5>Closure</h5>
                                    <p>Report</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsClosureOpen(true)}
                                title="View closure report"
                            >
                                View
                            </button>
                        </div>
                    )}
                    {/* Modal with PDF viewer */}
                    <PdfViewerModal
                        isOpen={isModalOpen}
                        onClose={closeModal}
                        fileUrl={getDefectReportUrl(defect)}
                    />
                    {remedialRef && (
                        <PdfViewerModal
                            isOpen={isRemedialOpen}
                            onClose={() => setIsRemedialOpen(false)}
                            fileUrl={`/remedial_reports/${remedialRef}`}
                        />
                    )}

                    {closureRef && (
                        <PdfViewerModal
                            isOpen={isClosureOpen}
                            onClose={() => setIsClosureOpen(false)}
                            fileUrl={getClosureReportUrl(defect)}
                        />
                    )}

                    <PointerraModal
                        isOpen={is3DOpen}
                        onClose={() => setIs3DOpen(false)}
                        embedUrl={embedUrl}
                        subtitle={defect.asset_name || defect.asset_id}
                    />
                </div>
            </div>
        </div>
    );
}

export default DefectDetails;
